import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiErrorResponse } from "../models/ApiResponse";
import { BatchStaleness } from "../models/BatchStaleness";
import Invoice, { CreditInvoiceResponse, InvoicePartial } from "../models/Invoice";
import InvoiceBatch, {
	CountInvoiceBatchScopeRequest,
	CountInvoiceBatchScopeResponse,
	CreateInvoiceBatchRequest,
	InvoiceBatchListItem,
} from "../models/InvoiceBatch";
import { InvoiceDownload } from "../models/InvoiceDownload";
import {
	INVOICE_BATCH_SORTING_COLUMN,
	INVOICE_SORTING_COLUMN,
	SORT_ORDER,
} from "../models/Sorting";
import { removeUndefined } from "../utils/object.utils";
import { apiStore } from "../stores/api.store";
import { toast } from "../utils/toast";

export type GetInvoicesParams = {
	page: number;
	pageSize: number;
	keyword?: string;
	sort?: INVOICE_SORTING_COLUMN;
	order?: SORT_ORDER;
};

export type GetInvoiceBatchesParams = {
	page: number;
	pageSize: number;
	keyword?: string;
	sort?: INVOICE_BATCH_SORTING_COLUMN;
	order?: SORT_ORDER;
};

export type UpdateInvoiceParams = {
	include?: boolean;
	customInvoiceRows?: {
		description: string;
		price?: number;
		quantity?: number;
		vatPercentage?: number;
	}[];
};

/** Coerce the API's date strings to Date instances (batch form works with Dates). */
export function parseInvoiceBatch(invoiceBatch: InvoiceBatch): InvoiceBatch {
	return {
		...invoiceBatch,
		to: new Date(invoiceBatch.to),
		invoiceDate: new Date(invoiceBatch.invoiceDate),
	};
}

export function useInvoices(query: GetInvoicesParams) {
	const { data, ...rest } = useQuery(
		["invoices", query],
		() => {
			const q: Record<string, any> = { ...query };
			removeUndefined(q);

			return apiStore
				.get<{ totalCount: number; invoices: InvoicePartial[] }>({
					path: `/invoice`,
					query: q,
					isAuthRequired: true,
				})
				.then((d) => d.data);
		},
		{ keepPreviousData: true }
	);

	return {
		invoices: data?.invoices ?? [],
		totalInvoicesCount: data?.totalCount ?? 0,
		...rest,
	};
}

export function useInvoice(invoiceId?: string, expand?: string[]) {
	const { data, ...rest } = useQuery(
		["invoices", invoiceId, { expand: expand ?? [] }],
		() =>
			apiStore
				.get<Invoice>({
					path: `/invoice/${invoiceId}`,
					isAuthRequired: true,
					query: {
						expand: expand ? expand.join(",") : "",
					},
				})
				.then((d) => d.data),
		{
			enabled: !!invoiceId,
			onError: (e: any) => toast.error(e?.error?.detail),
		}
	);

	return {
		invoice: data,
		...rest,
	};
}

export function useInvoiceBatches(query: GetInvoiceBatchesParams) {
	const { data, ...rest } = useQuery(
		["invoice-batches", query],
		() => {
			const q: Record<string, any> = { ...query };
			removeUndefined(q);

			return apiStore
				.get<{ totalCount: number; invoiceBatches: InvoiceBatchListItem[] }>({
					path: `/invoice-batch`,
					query: q,
					isAuthRequired: true,
				})
				.then((d) => d.data);
		},
		{ keepPreviousData: true }
	);

	return {
		invoiceBatches: data?.invoiceBatches ?? [],
		totalInvoiceBatchCount: data?.totalCount ?? 0,
		...rest,
	};
}

/**
 * Imperative fetch of a single invoice batch (GET as mutation, like
 * usePollSalaryBatch): the batch form polls this during preview creation and
 * processing, and keeps the result as page-local draft state.
 */
export function useFetchInvoiceBatch() {
	return useMutation<InvoiceBatch, ApiErrorResponse, { invoiceBatchId: string; expand?: string[] }>(
		({ invoiceBatchId, expand }) =>
			apiStore
				.get<InvoiceBatch>({
					path: `/invoice-batch/${invoiceBatchId}`,
					isAuthRequired: true,
					query: { expand: expand ? expand.join(",") : "invoices" },
				})
				.then((d) => d.data)
	);
}

/**
 * Projects how many customers and time reports an invoice batch would include,
 * without creating one. Backs the live readout on the batch form so the size of
 * a run is visible before committing to it.
 *
 * Disabled until a cut-off date is picked, rather than asking the backend to
 * count an unbounded period.
 */
export function useInvoiceBatchScopeCount(request?: CountInvoiceBatchScopeRequest) {
	const { data, ...rest } = useQuery(
		["invoice-batch-scope-count", request],
		() =>
			apiStore
				.post<CountInvoiceBatchScopeResponse>({
					path: `/invoice-batch/count`,
					body: request,
					isAuthRequired: true,
				})
				.then((d) => {
					const data = d.data;

					// A backend from before the { counts, customers } reshape
					// answers with the old flat shape. Treat it as "no
					// projection" instead of letting consumers crash on
					// data.counts.* / data.customers.filter during render.
					if (
						typeof data?.counts?.invoiceableTimeReports !== "number" ||
						!Array.isArray(data?.customers)
					)
						return undefined;

					return data;
				}),
		{ enabled: !!request?.to, keepPreviousData: true }
	);

	return { scopeCount: data, ...rest };
}

export function useCreateInvoiceBatch() {
	const queryClient = useQueryClient();

	return useMutation<InvoiceBatch, ApiErrorResponse, CreateInvoiceBatchRequest>(
		(body) =>
			apiStore
				.post<InvoiceBatch>({
					path: `/invoice-batch`,
					body,
					isAuthRequired: true,
				})
				.then((d) => parseInvoiceBatch(d.data)),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["invoice-batches"]);
			},
		}
	);
}

export function useProcessInvoiceBatch() {
	const queryClient = useQueryClient();

	return useMutation<InvoiceBatch, ApiErrorResponse, string>(
		(invoiceBatchId) =>
			apiStore
				.post<InvoiceBatch>({
					path: `/invoice-batch/${invoiceBatchId}/process`,
					isAuthRequired: true,
				})
				.then((d) => parseInvoiceBatch(d.data)),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["invoice-batches"]);
			},
		}
	);
}

/**
 * Starts a dry run ("Testkör") of a PREVIEW batch: the invoice system is read
 * and checked but nothing is written, and the batch stays PREVIEW. Answers the
 * batch with `dryRun.status` RUNNING; poll the batch until it leaves RUNNING.
 * 409 `DRY_RUN_IN_PROGRESS` when one is already running.
 */
export function useDryRunInvoiceBatch() {
	return useMutation<InvoiceBatch, ApiErrorResponse, string>((invoiceBatchId) =>
		apiStore
			.post<InvoiceBatch>({
				path: `/invoice-batch/${invoiceBatchId}/dry-run`,
				isAuthRequired: true,
			})
			.then((d) => parseInvoiceBatch(d.data))
	);
}

export function useRollbackInvoiceBatch() {
	const queryClient = useQueryClient();

	return useMutation<
		InvoiceBatch,
		ApiErrorResponse,
		{ invoiceBatchId: string; deleteAfterRollback?: boolean }
	>(
		({ invoiceBatchId, deleteAfterRollback }) =>
			apiStore
				.post<InvoiceBatch>({
					path: `/invoice-batch/${invoiceBatchId}/rollback`,
					body: { deleteAfterRollback },
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["invoice-batches"]);
			},
		}
	);
}

/**
 * Rebuild a draft (PREVIEW or FAILED) batch's preview in place. The backend
 * answers with the batch reset to INITIAL — poll it for the new outcome, the
 * same as after creating a preview.
 */
export function useRefreshInvoiceBatch() {
	const queryClient = useQueryClient();

	return useMutation<InvoiceBatch, ApiErrorResponse, string>(
		(invoiceBatchId) =>
			apiStore
				.post<InvoiceBatch>({
					path: `/invoice-batch/${invoiceBatchId}/refresh`,
					isAuthRequired: true,
				})
				.then((d) => parseInvoiceBatch(d.data)),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["invoice-batches"]);
				queryClient.invalidateQueries(["invoice-batch-staleness"]);
			},
		}
	);
}

/**
 * Whether the time reports behind a draft's preview drifted since it was
 * computed. Only meaningful for a PREVIEW batch, so callers gate on that.
 * A backend without the endpoint (not yet deployed) reads as "not stale".
 */
export function useInvoiceBatchStaleness(invoiceBatchId?: string, enabled = true) {
	const { data, ...rest } = useQuery(
		["invoice-batch-staleness", invoiceBatchId],
		() =>
			apiStore
				.get<BatchStaleness>({
					path: `/invoice-batch/${invoiceBatchId}/staleness`,
					isAuthRequired: true,
				})
				.then((d) => d.data)
				.catch(() => undefined),
		{ enabled: !!invoiceBatchId && enabled }
	);

	return { staleness: data, ...rest };
}

/** Include/exclude an invoice in a batch or set its custom rows. */
export function useUpdateInvoiceInBatch() {
	return useMutation<
		InvoiceBatch,
		ApiErrorResponse,
		{ invoiceBatchId: string; invoiceId: string; change: UpdateInvoiceParams }
	>(({ invoiceBatchId, invoiceId, change }) =>
		apiStore
			.put<InvoiceBatch>({
				path: `/invoice-batch/${invoiceBatchId}/invoice/${invoiceId}`,
				body: change,
				isAuthRequired: true,
			})
			.then((d) => d.data)
	);
}

/** Fetch the base64 PDF for an invoice (Fortnox only). */
export function useDownloadInvoicePdf() {
	return useMutation<InvoiceDownload, ApiErrorResponse, string>((invoiceNum) =>
		apiStore
			.get<InvoiceDownload>({
				path: `/invoice/${invoiceNum}/download`,
				isAuthRequired: true,
			})
			.then((d) => d.data)
	);
}

export function useCreditInvoice() {
	const queryClient = useQueryClient();

	return useMutation<CreditInvoiceResponse, ApiErrorResponse, string>(
		(invoiceId) =>
			apiStore
				.post<CreditInvoiceResponse>({
					path: `/invoice/${invoiceId}/credit`,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["invoices"]);
			},
		}
	);
}

/**
 * Invalidations shared by the per-invoice export follow-up actions. The batch
 * form keeps its batch as page-local state, so it refetches itself on success;
 * the list (badge from `exportSummary`) and invoice queries go through here.
 */
function useInvalidateInvoiceExportFollowUp() {
	const queryClient = useQueryClient();

	return () => {
		queryClient.invalidateQueries(["invoice-batches"]);
		queryClient.invalidateQueries(["invoices"]);
	};
}

/**
 * "Finns inte i Visma": the admin checked the invoice system and the held
 * invoice was never created, so its time reports are released for a new batch.
 */
export function useReleaseInvoiceTimeReports() {
	const invalidate = useInvalidateInvoiceExportFollowUp();

	return useMutation<Invoice, ApiErrorResponse, string>(
		(invoiceId) =>
			apiStore
				.post<Invoice>({
					path: `/invoice/${invoiceId}/release-time-reports`,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{ onSuccess: invalidate }
	);
}

/**
 * "Finns i Visma": the held invoice was created after all; the admin enters the
 * invoice system's number and the invoice becomes PROCESSED.
 */
export function useConfirmInvoiceExported() {
	const invalidate = useInvalidateInvoiceExportFollowUp();

	return useMutation<Invoice, ApiErrorResponse, { invoiceId: string; invoiceNumber: string }>(
		({ invoiceId, invoiceNumber }) =>
			apiStore
				.post<Invoice>({
					path: `/invoice/${invoiceId}/confirm-exported`,
					body: { invoiceNumber },
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{ onSuccess: invalidate }
	);
}

/** The specification PDF has been attached by hand in the invoice system. */
export function useResolveInvoiceAttachment() {
	const invalidate = useInvalidateInvoiceExportFollowUp();

	return useMutation<Invoice, ApiErrorResponse, string>(
		(invoiceId) =>
			apiStore
				.post<Invoice>({
					path: `/invoice/${invoiceId}/attachment-resolved`,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{ onSuccess: invalidate }
	);
}

export interface RefreshSingleCustomerDeliveryStateResponse {
	/** ISO time of the read from Fortnox. */
	observedAt: string;
	/** False when the customer does not exist in Fortnox yet; nothing was written then. */
	inFortnox: boolean;
	/** True when Fortnox has an active e-invoice connection for the customer. */
	managed: boolean;
	deliveryMethod?: "E_INVOICE_GLN" | "E_INVOICE_ORG";
	gln?: string;
	/** True when the Origo customer changed because of this read. */
	updated: boolean;
}

/**
 * "Hämta e-fakturakoppling från Fortnox" on one customer: same write-back as
 * the org-level refresh, for this customer only. Refetches the customer so
 * the invoice tab picks up the new lock/method/GLN.
 */
export function useRefreshFortnoxCustomerDeliveryStateForCustomer(customerId?: string) {
	const queryClient = useQueryClient();

	return useMutation<RefreshSingleCustomerDeliveryStateResponse, ApiErrorResponse, void>(
		() =>
			apiStore
				.post<RefreshSingleCustomerDeliveryStateResponse>({
					path: `/fortnox/customer/${customerId}/refresh-delivery-state`,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: (res) => {
				if (res.updated) {
					queryClient.invalidateQueries(["customers", customerId]);
					queryClient.invalidateQueries(["customers"]);
				}
			},
			onError: ({ error }) => {
				toast.apiError(error?.code);
			},
		}
	);
}

export interface RefreshCustomerDeliveryStateResponse {
	/** ISO time of the read from Fortnox. */
	observedAt: string;
	total: number;
	/** Customers whose delivery method Fortnox now owns (e-invoice active). */
	managed: number;
	/** Customers whose delivery state changed in Origo. */
	updated: number;
	unchanged: number;
}

/**
 * "Hämta från Fortnox": reads every customer's delivery type and GLN from
 * Fortnox and writes the Fortnox-managed state back onto the Origo customers
 * (same code path as the invoice export). Fails like the other fortnox
 * endpoints when the org has no active Fortnox invoice integration.
 */
export function useRefreshFortnoxCustomerDeliveryState() {
	const queryClient = useQueryClient();

	return useMutation<RefreshCustomerDeliveryStateResponse, ApiErrorResponse, void>(
		() =>
			apiStore
				.post<RefreshCustomerDeliveryStateResponse>({
					path: `/fortnox/refresh-customer-delivery-state`,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["customers"]);
				queryClient.invalidateQueries(["corporate-groups"]);
			},
			onError: ({ error }) => {
				toast.apiError(error?.code);
			},
		}
	);
}
